// Bottom sheet MOBILE de "Añadí referencias" — la única puerta a los
// adjuntos en mobile, detrás de una sola card en el empty state.
//
// Ocupa casi toda la pantalla siempre (no se adapta al contenido): las tres
// pestañas tienen alturas muy distintas y un sheet que crece y se achica al
// cambiar de tab se siente inestable.
//
// Tres pestañas: Subidas (default) / Productos / Modelos.
//   • Productos → monta `PickProductPanel`, LA MISMA pantalla de "seleccionar
//     producto" de los modos de InterfazV2 (mismo montaje que usa
//     PresetDetailSheet en mobile: variant="desktop" + hideBack +
//     scrollHeader). Trae gratis buscador, orden, "último utilizado",
//     sugeridos vs otros, y su footer de Confirmar.
//   • Modelos  → grilla propia: 2 columnas, tile más alto que ancho (3:4, que
//     es como se ve una persona) y el nombre chico debajo.
//   • Subidas  → card de subir + las imágenes ya adjuntas.
//
// CARGA PROGRESIVA: las imágenes son `CachedImg`, que ya hace lazy load con
// IntersectionObserver (no baja bytes hasta acercarse al viewport). Además la
// grilla de modelos renderiza de a tandas (ver MODELS_PAGE) y suma más al
// llegar al final, así el DOM tampoco se llena de una.
//
// El chrome (backdrop + panel + handle) es el del sheet del menú "+".

import React, { useState, useEffect, useRef } from 'react';
import { UploadCloud, Check, X } from 'lucide-react';
import { AccionCard } from '../../components/AccionCard';
import type { ChatAttachment } from '../../agentAttachments';
import type { MockProductV2 } from '../../mockData';
import type { ModelV2 } from '../../modelsApi';
import type { ModelSelection } from '../../ModelPicker';
import CachedImg from '../../../../components/CachedImg';
import { PickProductPanel } from '../../components/PickProductPanel';

type Tab = 'subidas' | 'productos' | 'modelos';

const TABS: { id: Tab; label: string }[] = [
  { id: 'subidas', label: 'Subidas' },
  { id: 'productos', label: 'Productos' },
  { id: 'modelos', label: 'Modelos' },
];

/** Cuántos modelos se renderizan por tanda al scrollear. */
const MODELS_PAGE = 12;

export interface ReferenciasSheetProps {
  open: boolean;
  onClose: () => void;
  attachments: ChatAttachment[];
  onRemoveAttachment: (id: string) => void;
  models: ModelV2[];
  onUploadImage: () => void;
  /** "Utilizar": commit ÚNICO de todo lo elegido en las pestañas. `model` en
   *  `null` significa "no se tocó el modelo", no "quitar el modelo". */
  onUtilizar: (seleccion: {
    products: MockProductV2[];
    model: ModelSelection | null;
  }) => void;
  uploading?: boolean;
}

export const ReferenciasSheet: React.FC<ReferenciasSheetProps> = ({
  open,
  onClose,
  attachments,
  onRemoveAttachment,
  models,
  onUploadImage,
  onUtilizar,
  uploading = false,
}) => {
  const [tab, setTab] = useState<Tab>('subidas');
  const [modelsShown, setModelsShown] = useState(MODELS_PAGE);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // ── Selección en BORRADOR ────────────────────────────────────────────
  // Nada se adjunta al tocar: se acumula acá y recién "Utilizar" lo comitea
  // todo junto. Así se puede elegir productos, cambiar a Modelos, elegir uno,
  // y que al confirmar entre TODO. (Las subidas son la excepción: subir un
  // archivo ya lo deja adjuntado, porque la subida en sí es la acción.)
  const [draftProducts, setDraftProducts] = useState<MockProductV2[]>([]);
  const [draftModel, setDraftModel] = useState<ModelSelection | null>(null);

  // Al cerrar: se resetea la tanda de modelos (reabrirlo con 200 montados
  // sería justo lo que la paginación evita) y el borrador.
  useEffect(() => {
    if (!open) {
      setModelsShown(MODELS_PAGE);
      setDraftProducts([]);
      setDraftModel(null);
    }
  }, [open]);

  // Sentinela al final de la grilla de modelos: al entrar en viewport, suma
  // otra tanda. Sin esto el DOM monta el catálogo entero de una.
  useEffect(() => {
    if (!open || tab !== 'modelos') return;
    const el = sentinelRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setModelsShown((n) => Math.min(models.length, n + MODELS_PAGE));
        }
      },
      { rootMargin: '200px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [open, tab, models.length, modelsShown]);

  if (!open) return null;

  const imagenes = attachments.filter(
    (a): a is Extract<ChatAttachment, { kind: 'image' }> => a.kind === 'image',
  );
  const productoIds = attachments
    .filter(
      (a): a is Extract<ChatAttachment, { kind: 'product' }> =>
        a.kind === 'product',
    )
    .map((a) => a.productId);
  const modeloSel = attachments.find(
    (a): a is Extract<ChatAttachment, { kind: 'model' }> => a.kind === 'model',
  );
  const modeloAdjuntoId =
    modeloSel && modeloSel.selection.mode === 'preset'
      ? String(modeloSel.selection.id)
      : null;
  // El borrador manda sobre lo ya adjunto (es lo que el user acaba de tocar).
  const modeloId =
    draftModel && draftModel.mode === 'preset'
      ? String(draftModel.id)
      : modeloAdjuntoId;

  const totalSeleccionado = draftProducts.length + (draftModel ? 1 : 0);

  return (
    <div className="md:hidden fixed inset-0 z-[62]">
      <div
        className="absolute inset-0 bg-black/40 animate-fade-in"
        onClick={onClose}
      />
      {/* Casi toda la pantalla, alto FIJO: deja ver un poco del fondo arriba
          para que se lea como una capa encima, no como una pantalla nueva. */}
      <div
        className="absolute inset-x-0 bottom-0 top-[7dvh] bg-white rounded-t-[20px] shadow-2xl flex flex-col"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="shrink-0 px-3 pt-3">
          <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto mb-3" />
          {/* Pestañas */}
          <div className="flex items-center gap-1.5 mb-3 px-0.5">
            {TABS.map((t) => {
              const active = t.id === tab;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTab(t.id)}
                  className={`flex-1 inline-flex items-center justify-center h-9 rounded-full text-[14px] font-semibold transition-colors ${
                    active
                      ? 'bg-brand-primary text-white'
                      : 'bg-neutral-100 text-[#02062BB3]'
                  }`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Productos: la pantalla de seleccionar producto de los modos ──
            `variant="desktop"` + hideBack + scrollHeader es el MISMO montaje
            que usa PresetDetailSheet para su pantalla mobile full-screen. */}
        {tab === 'productos' ? (
          <div className="flex-1 min-h-0 flex flex-col">
            <PickProductPanel
              variant="desktop"
              hideBack
              scrollHeader
              initialSelectedIds={productoIds}
              // El footer propio del panel se oculta: el CTA único es el
              // "Utilizar" del sheet, común a las tres pestañas. La selección
              // llega en vivo por `onSelectionChange`.
              hideFooter
              onSelectionChange={setDraftProducts}
              onConfirm={() => {
                /* no se usa: confirma el botón del sheet */
              }}
              onBack={() => setTab('subidas')}
              // Sin título: el sheet ya tiene su encabezado con pestañas
              // arriba, y repetirlo comía alto de pantalla.
              hideHeader
            />
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-y-auto px-3 pb-3">
            {tab === 'subidas' && (
              <div className="grid grid-cols-2 gap-3">
                {/* Card de subir — primera de la grilla. Usa AccionCard, que
                    ya ES este patrón (ícono en círculo + label debajo). */}
                <AccionCard
                  className="aspect-square"
                  label="Subir imagen"
                  icon={<UploadCloud className="w-5 h-5" strokeWidth={2} />}
                  onClick={onUploadImage}
                  loading={uploading}
                  loadingLabel="Subiendo…"
                />
                {imagenes.map((a) => (
                  <div
                    key={a.id}
                    className="relative aspect-square rounded-xl overflow-hidden border-2 border-brand-primary"
                  >
                    <CachedImg
                      src={a.previewUrl}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => onRemoveAttachment(a.id)}
                      aria-label="Quitar"
                      className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/55 text-white inline-flex items-center justify-center"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {tab === 'modelos' && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  {models.slice(0, modelsShown).map((m) => {
                    const selected = modeloId === String(m.id);
                    return (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() =>
                          // Al borrador, no al chat: se comitea con "Utilizar".
                          // Tocar el ya elegido lo deselecciona.
                          setDraftModel((prev) =>
                            prev &&
                            prev.mode === 'preset' &&
                            String(prev.id) === String(m.id)
                              ? null
                              : {
                                  mode: 'preset',
                                  id: String(m.id),
                                  name: m.nombre,
                                  imageUrl: m.thumbnailUrl,
                                },
                          )
                        }
                        className={`rounded-2xl overflow-hidden border-2 text-left transition-colors active:scale-[0.98] ${
                          selected
                            ? 'border-brand-primary'
                            : 'border-neutral-200 hover:border-brand-primary/40'
                        }`}
                      >
                        {/* 3:4 — una persona se lee mejor en vertical que en
                            un cuadrado. */}
                        <div className="relative aspect-[3/4] bg-neutral-100">
                          <CachedImg
                            src={m.thumbnailUrl}
                            alt={m.nombre}
                            loading="lazy"
                            className="absolute inset-0 w-full h-full object-cover"
                          />
                          {selected && (
                            <span className="absolute top-2 right-2 w-6 h-6 rounded-full bg-brand-primary text-white inline-flex items-center justify-center shadow">
                              <Check className="w-3.5 h-3.5" strokeWidth={3} />
                            </span>
                          )}
                        </div>
                        <div className="px-2.5 py-2">
                          <span className="block text-[12.5px] font-medium text-[#02062BE6] truncate">
                            {m.nombre}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
                {/* Sentinela de la carga progresiva. */}
                {modelsShown < models.length && (
                  <div
                    ref={sentinelRef}
                    className="h-10 flex items-center justify-center"
                  >
                    <span className="w-5 h-5 rounded-full border-2 border-brand-primary/30 border-t-brand-primary animate-spin" />
                  </div>
                )}
                {models.length === 0 && (
                  <p className="text-[12.5px] text-[#3C3C43]/55 leading-snug py-6 text-center">
                    Todavía no tenés modelos guardados.
                  </p>
                )}
              </>
            )}
          </div>
        )}

        {/* CTA único de las TRES pestañas. Comitea el borrador completo
            (productos + modelo) sin importar en cuál estés parado, que es lo
            que permite elegir productos, saltar a Modelos y confirmar todo de
            una. El footer propio del PickProductPanel va oculto. */}
        <div className="shrink-0 px-3 pt-2 pb-3 border-t border-black/[0.05] bg-white">
          <button
            type="button"
            onClick={() => {
              onUtilizar({ products: draftProducts, model: draftModel });
              onClose();
            }}
            className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-2xl bg-brand-primary text-white text-[15px] font-semibold transition-colors active:opacity-90"
          >
            <span>Utilizar</span>
            {totalSeleccionado > 0 && (
              <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full bg-white/25 text-[12px] font-bold leading-none">
                {totalSeleccionado}
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReferenciasSheet;
